import { getApiUrl } from '../utils/config';

export interface PricingFeature {
  id: string;
  name: string;
  description: string;
  icon?: string;
  category: string;
  feature_type: 'boolean' | 'limit' | 'text';
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface TierPricingFeature {
  id: string;
  tier_id: string;
  pricing_feature_id: string;
  value?: string;
  is_included: boolean;
  is_highlighted: boolean;
  display_text?: string;
  badge_text?: string;
  badge_color?: string;
  show_in_comparison?: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
  pricing_feature: PricingFeature;
}

export interface TierWithFeatures {
  id: string;
  name: string;
  display_name: string;
  description?: string;
  price_monthly?: number;
  price_yearly?: number;
  is_active: boolean;
  sort_order: number;
  features: TierPricingFeature[];
}

export interface PricingPlanResponse {
  tiers: TierWithFeatures[];
  feature_categories: string[];
}

export interface PricingFeatureCreate {
  name: string;
  description: string;
  icon?: string;
  category: string;
  feature_type?: 'boolean' | 'limit' | 'text';
  is_active?: boolean;
  sort_order?: number;
}

export interface PricingFeatureUpdate {
  name?: string;
  description?: string;
  icon?: string;
  category?: string;
  feature_type?: 'boolean' | 'limit' | 'text';
  is_active?: boolean;
  sort_order?: number;
}

export interface TierPricingFeatureCreate {
  tier_id: string;
  pricing_feature_id: string;
  value?: string;
  is_included?: boolean;
  is_highlighted?: boolean;
  display_text?: string;
  badge_text?: string;
  badge_color?: string;
  show_in_comparison?: boolean;
  sort_order?: number;
}

export interface TierPricingFeatureUpdate {
  value?: string;
  is_included?: boolean;
  is_highlighted?: boolean;
  display_text?: string;
  badge_text?: string;
  badge_color?: string;
  show_in_comparison?: boolean;
  sort_order?: number;
}

class AdminPricingService {
  private getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  // Pricing Features Management
  async getPricingFeatures(category?: string, isActive?: boolean): Promise<PricingFeature[]> {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (isActive !== undefined) params.append('is_active', isActive.toString());

    const response = await fetch(`${getApiUrl()}/api/v1/admin/pricing-features?${params}`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch pricing features');
    }

    return response.json();
  }

  async createPricingFeature(featureData: PricingFeatureCreate): Promise<PricingFeature> {
    const response = await fetch(`${getApiUrl()}/api/v1/admin/pricing-features`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(featureData),
    });

    if (!response.ok) {
      throw new Error('Failed to create pricing feature');
    }

    return response.json();
  }

  async updatePricingFeature(featureId: string, featureData: PricingFeatureUpdate): Promise<PricingFeature> {
    const response = await fetch(`${getApiUrl()}/api/v1/admin/pricing-features/${featureId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(featureData),
    });

    if (!response.ok) {
      throw new Error('Failed to update pricing feature');
    }

    return response.json();
  }

  async deletePricingFeature(featureId: string): Promise<void> {
    const response = await fetch(`${getApiUrl()}/api/v1/admin/pricing-features/${featureId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to delete pricing feature');
    }
  }

  // Tier Feature Assignments
  async getTierFeatures(tierId: string): Promise<TierPricingFeature[]> {
    const response = await fetch(`${getApiUrl()}/api/v1/admin/tiers/${tierId}/features`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch tier features');
    }

    return response.json();
  }

  async assignFeatureToTier(tierId: string, featureData: TierPricingFeatureCreate): Promise<TierPricingFeature> {
    const response = await fetch(`${getApiUrl()}/api/v1/admin/tiers/${tierId}/features`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(featureData),
    });

    if (!response.ok) {
      throw new Error('Failed to assign feature to tier');
    }

    return response.json();
  }

  async updateTierFeature(tierFeatureId: string, featureData: TierPricingFeatureUpdate): Promise<TierPricingFeature> {
    const response = await fetch(`${getApiUrl()}/api/v1/admin/tier-features/${tierFeatureId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(featureData),
    });

    if (!response.ok) {
      throw new Error('Failed to update tier feature');
    }

    return response.json();
  }

  async removeFeatureFromTier(tierFeatureId: string): Promise<void> {
    const response = await fetch(`${getApiUrl()}/api/v1/admin/tier-features/${tierFeatureId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to remove feature from tier');
    }
  }

  // Public API
  async getPricingPlans(): Promise<PricingPlanResponse> {
    const response = await fetch(`${getApiUrl()}/api/v1/public/pricing-plans`);

    if (!response.ok) {
      throw new Error('Failed to fetch pricing plans');
    }

    return response.json();
  }

  async getFeatureCategories(): Promise<string[]> {
    const response = await fetch(`${getApiUrl()}/api/v1/admin/feature-categories`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch feature categories');
    }

    return response.json();
  }
}

export default new AdminPricingService();